import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import {
  Alert,
  Modal,
  Radio,
  Select,
  notification,
} from 'antd';
import { ze } from 'utils/zhEn';
import { upsertServerAppSettings } from 'reducers/app/appActions';
import {
  TAPD_FOLLOW_DEFAULT_TEMPLATE_VALUE,
  TAPD_NO_TEMPLATE_VALUE,
  findTapdDefaultTemplateId,
} from 'utils/tapd-template-form-util';

/**
 * 嵌套在提单弹窗（或特征分析规则配置弹窗）里的「设置默认模板」弹窗。
 * antd4 的 Select 下拉 z-index 是 1050，比 Modal 的 1000 高，所以这里必须显式抬高 zIndex。
 */
export const TAPD_DEFAULT_TEMPLATE_MODAL_Z_INDEX = 1100;

const MODE_FOLLOW = 'FOLLOW';
const MODE_NONE = 'NONE';
const MODE_SPECIFIED = 'SPECIFIED';

export function parseDefaultTemplateSetting(settingValue, templateList) {
  if (settingValue === undefined || settingValue === null || settingValue === ''
    || String(settingValue) === TAPD_FOLLOW_DEFAULT_TEMPLATE_VALUE) {
    return { mode: MODE_FOLLOW, templateId: findTapdDefaultTemplateId(templateList) };
  }
  if (String(settingValue) === TAPD_NO_TEMPLATE_VALUE) {
    return { mode: MODE_NONE, templateId: '' };
  }
  return { mode: MODE_SPECIFIED, templateId: String(settingValue) };
}

export function makeDefaultTemplateSettingValue(mode, templateId) {
  if (mode === MODE_NONE) {
    return TAPD_NO_TEMPLATE_VALUE;
  }
  if (mode === MODE_SPECIFIED) {
    return templateId || TAPD_FOLLOW_DEFAULT_TEMPLATE_VALUE;
  }
  return TAPD_FOLLOW_DEFAULT_TEMPLATE_VALUE;
}

const TapdDefaultTemplateModal = ({
  visible,
  appId,
  settingKey,
  settingValue,
  templateList,
  description,
  onCancel,
  onSaved,
}) => {
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState(MODE_FOLLOW);
  const [templateId, setTemplateId] = useState('');

  useEffect(() => {
    if (!visible) {
      return;
    }
    const parsed = parseDefaultTemplateSetting(settingValue, templateList);
    setMode(parsed.mode);
    setTemplateId(parsed.templateId);
    // 只依赖 visible：模板列表刷新时不要把用户刚选的选项重置掉
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const list = Array.isArray(templateList) ? templateList : [];
  const configuredIdMissing = mode === MODE_SPECIFIED && templateId
    && !list.some(x => x && String(x.id) === String(templateId));

  async function onSubmit() {
    const value = makeDefaultTemplateSettingValue(mode, templateId);
    setLoading(true);
    try {
      await dispatch(upsertServerAppSettings(appId, {
        [settingKey]: value,
      }));
      notification.success({ message: ze('默认模板设置保存成功', 'Default template saved.') });
      onSaved(value);
    } catch (e) {
      notification.error({ message: ze('默认模板设置保存失败', 'Failed to save the default template.') });
    } finally {
      setLoading(false);
    }
  }

  return <Modal
    title={ze('设置默认提单模板', 'Set Default Template')}
    visible={visible}
    width='560px'
    zIndex={TAPD_DEFAULT_TEMPLATE_MODAL_Z_INDEX}
    maskClosable={false}
    okText={ze('保存', 'Save')}
    okButtonProps={{ disabled: loading }}
    onCancel={() => onCancel()}
    onOk={() => onSubmit()}
  >
    <div style={{ marginBottom: '12px' }}>
      { description || ze('项目级设置，手动提单和特征分析自动提单共用。保存后，该项目下打开提单表单（手动提单、或新建特征分析自动提单规则）时会初始选中这里设置的模板；表单里仍可以临时改成其它模板。', 'App-level setting, shared by manual bug creation and feature-analysis auto bug creation. After saving, the bug creation form (manual, or a new feature-analysis rule) will preselect the template set here; it can still be changed temporarily in the form.') }
    </div>
    { configuredIdMissing && <Alert
      style={{ marginBottom: '12px' }}
      type='warning'
      showIcon
      message={ze('当前配置的模板在 TAPD 侧已不存在，表单已回退为跟随 TAPD 默认模板。保存后将按本次选择生效。', 'The configured template no longer exists in TAPD, so the form has fallen back to the TAPD default. Saving will apply your current choice.')}
    /> }
    <Radio.Group
      value={mode}
      onChange={(e) => {
        const nextMode = e.target.value;
        setMode(nextMode);
        if (nextMode === MODE_SPECIFIED && !templateId) {
          setTemplateId(findTapdDefaultTemplateId(list));
        }
      }}
    >
      <div style={{ marginBottom: '8px' }}>
        <Radio value={MODE_FOLLOW}>{ ze('跟随 TAPD 项目默认模板', 'Follow TAPD project default template') }</Radio>
      </div>
      <div style={{ marginBottom: '8px' }}>
        <Radio value={MODE_NONE}>{ ze('不使用模板', 'No template') }</Radio>
      </div>
      <div style={{ marginBottom: '8px' }}>
        <Radio value={MODE_SPECIFIED}>{ ze('指定模板', 'Specify a template') }</Radio>
      </div>
    </Radio.Group>
    { mode === MODE_SPECIFIED && <Select
      style={{ width: '100%' }}
      placeholder={ze('请选择模板', 'Please select a template')}
      options={list.map(x => ({ value: String(x.id), label: x.name }))}
      value={templateId || undefined}
      onChange={(v) => setTemplateId(v || '')}
      // 下拉默认挂 body（z-index 1050），会被本弹窗（zIndex 1100）的 wrap 盖住导致点不动，
      // 所以挂到弹窗内部的 DOM 上，跟随本弹窗的层叠上下文。
      getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}
    /> }
  </Modal>;
};

TapdDefaultTemplateModal.propTypes = {
  visible: PropTypes.bool,
  appId: PropTypes.string,
  settingKey: PropTypes.string,
  settingValue: PropTypes.string,
  templateList: PropTypes.array,
  description: PropTypes.node,
  onCancel: PropTypes.func,
  onSaved: PropTypes.func,
};

export default TapdDefaultTemplateModal;
